import { call, put, takeEvery, takeLatest, select, delay, take, fork, cancel } from 'redux-saga/effects';
import { listWatchlist } from '../../services/authService';
// Pause REST polling while Socket.IO ticks are flowing (see marketSocketSaga).
import { isMarketSocketConnected, waitForConnection } from '../../services/socket/marketSocket';

import {
  fetchWatchlistRequest,
  fetchWatchlistSuccess,
  fetchWatchlistFailure,
  deleteWatchlistRequest,
  deleteWatchlistSuccess,
  deleteWatchlistFailure,
  fetchMarketForWatchListRequest,
  fetchMarketForWatchListSuccess,
  fetchMarketForWatchListFailure,
  updateMarketWatchListData,
} from '../reducers/watchlistReducer';
import {
  fetchMarketDataRequest,
  startMarketDataUpdates,
  updateMarketData,
} from '../reducers/marketReducer';
import { getMarketData, deleteWatchlist } from '../../services/allApiService';
import { useSelector, useDispatch } from 'react-redux';

function* handleFetchWatchlist() {
  try {
    const activeTab = yield select(state => state.watchlist.activeTab);

    // `list-watchlist` returns EVERY exchange in a single payload, so once it's
    // loaded a tab switch doesn't need another network round-trip — we just
    // re-derive the new tab's tokens from the cached data. Only hit REST on the
    // first load or after the cache is cleared (page unmount / delete). This is
    // what keeps fast NFO⇄MCX switching snappy instead of firing a full
    // list-watchlist call on every tap.
    let watchlistData = yield select(state => state.watchlist.watchlistData);
    const hasCache = watchlistData && Object.keys(watchlistData).length > 0;

    if (!hasCache) {
      const response = yield call(listWatchlist);

      if (!response || !response.success || !response.data) {
        yield put(fetchWatchlistFailure((response && response.message) || 'Failed to fetch watchlist'));
        return;
      }

      watchlistData = response.data;
      yield put(fetchWatchlistSuccess(response.data));
    }

    let symbolTokensArray = [];

    if (activeTab === 'NFO') {
      const equityTokens = watchlistData['EQUITY']?.map(item => item) || [];
      const equityIndexTokens = watchlistData['EQUITY(INDEX)']?.map(item => item) || [];

      symbolTokensArray = [...equityTokens, ...equityIndexTokens];
    } else {
      symbolTokensArray = watchlistData[activeTab]?.map(item => item) || [];
    }

    // Show most recently added scripts first (created_at descending).
    symbolTokensArray = symbolTokensArray.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

    if (symbolTokensArray.length > 0) {
      yield put(fetchMarketForWatchListRequest({ tab: activeTab, symbolTokensArray }));
    } else {
      // Empty tab: clear any leftover rows AND drop the loading flag (via the
      // success reducer) so the "No items" empty state shows instead of an
      // endless shimmer.
      yield put(fetchMarketForWatchListSuccess([]));
    }
  } catch (error) {
    yield put(fetchWatchlistFailure(error.message));
  }
}

function* handleDeleteWatchlist(action) {
  try {
    const watchlistId = action.payload;
    const activeTab = yield select(state => state.watchlist.activeTab);

    const response = yield call(deleteWatchlist, watchlistId);

    if (response.success) {
      yield put(deleteWatchlistSuccess({ watchlistId }));

      // Refresh the watchlist data
      yield put(fetchWatchlistRequest());
    } else {
      yield put(deleteWatchlistFailure(response.message || 'Failed to delete from watchlist'));
    }
  } catch (error) {
    yield put(deleteWatchlistFailure(error.message));
  }
}

function* handleFetchMarketForWatchList(action) {
  try {
    const { tab, symbolTokensArray } = action.payload;
    const tokens = symbolTokensArray.map(item => item.token);

    if (tokens.length === 0) return;

    // ── ONE-TIME snapshot: always do a single REST quote on load so the LAST
    // traded price shows immediately — even when the market is closed and the
    // socket has no live ticks to send (e.g. NFO after 3:30 PM). The socket
    // then takes over for live updates; the repeating REST poll stays disabled
    // while the socket is connected (see handleUpdateWatchlistMarketData).
    const mode = 'FULL';
    const exchangeTokens = { [tab]: tokens };
    const response = yield call(getMarketData, mode, exchangeTokens);

    // Tab-switch race guard: this REST quote can take ~1s. If the user switched
    // to another tab while it was in flight, DROP the result — otherwise the
    // previous exchange's rows (e.g. NFO) blink in over the current tab (MCX)
    // for a second before the correct data arrives.
    const currentTab = yield select(state => state.watchlist.activeTab);
    if (currentTab !== tab) return;

    if (response && response.data && response.data.fetched) {
      const data = response.data.fetched;

      // Preserve the order from symbolTokensArray (which should match API order)
      const sortedData = symbolTokensArray.map(symbolItem => {
        const matched = data.find(
          item => String(item.symbolToken) === String(symbolItem.token)
        );

        if (matched) {
          return {
            ...matched,
            script_id: symbolItem.script_id || null,
            exchange_id: symbolItem.exchange_id || null,
            watchlist_id: symbolItem.id || null,
            client_id: symbolItem.client_id || null,
            expiry: symbolItem.expiry || null,
            lots: symbolItem.lots || null,
          };
        }
        return null;
      }).filter(Boolean); // Remove any null entries

      yield put(fetchMarketForWatchListSuccess(sortedData));
    } else {
      yield put(fetchMarketForWatchListFailure('Invalid response format'));
    }
  } catch (error) {
    yield put(fetchMarketForWatchListFailure(error.message));
  }
}
function* handleUpdateWatchlistMarketData() {
  let firstRun = true;
  while (true) {
    try {
      // Skip REST poll while socket is delivering ticks (see marketSocketSaga).
      // First iteration waits out the socket connect window to avoid a quote.
      let socketUp;
      if (firstRun) { socketUp = yield call(waitForConnection); firstRun = false; }
      else { socketUp = isMarketSocketConnected(); }
      if (socketUp) {
        yield delay(800);
        continue;
      }

      const { activeTab, watchlistData } = yield select(state => state.watchlist);

      if (watchlistData) {
        let symbolTokensArray = [];

        if (activeTab === 'NFO') {
          const equityTokens = watchlistData['EQUITY']?.map(item => item) || [];
          const equityIndexTokens = watchlistData['EQUITY(INDEX)']?.map(item => item) || [];
          symbolTokensArray = [...equityTokens, ...equityIndexTokens];
        } else {
          symbolTokensArray = watchlistData[activeTab]?.map(item => item) || [];
        }

        // Show most recently added scripts first (created_at descending).
        symbolTokensArray = symbolTokensArray.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        const tokens = symbolTokensArray.map(item => item.token);

        if (tokens.length > 0) {
          const mode = 'FULL';
          const exchangeTokens = { [activeTab]: tokens };
          const response = yield call(getMarketData, mode, exchangeTokens);

          // Same tab-switch race guard as the on-demand fetch: `activeTab` was
          // read at the top of this iteration, so if the user switched tabs
          // while this poll request was in flight we must NOT write the old
          // tab's rows (that's the NFO→MCX blink). Re-check before dispatching.
          const currentTab = yield select(state => state.watchlist.activeTab);

          if (currentTab === activeTab && response && response.data && response.data.fetched) {
            const data = response.data.fetched;

            // Preserve the order from symbolTokensArray
            const sortedData = symbolTokensArray.map(symbolItem => {
              const matched = data.find(
                item => String(item.symbolToken) === String(symbolItem.token)
              );

              if (matched) {
                return {
                  ...matched,
                  script_id: symbolItem.script_id || null,
                  exchange_id: symbolItem.exchange_id || null,
                  client_id: symbolItem.client_id || null,
                  expiry: symbolItem.expiry || null,
                  watchlist_id: symbolItem.id || null,
                  lots: symbolItem.lots || null,
                };
              }
              return null;
            }).filter(Boolean);

            yield put(updateMarketWatchListData(sortedData));
          }
        }
      }
    } catch (error) {
      console.error('Error updating watchlist market data:', error);
    }
    yield delay(800);
  }
}

export default function* watchlistSaga() {
  // takeLatest (not takeEvery): a rapid tab switch cancels the previous, still
  // in-flight fetch chain so an older tab's REST response can never land after
  // and overwrite the current tab's data. This is the core fix for the laggy /
  // wrong "filling" when flicking between NFO and MCX quickly.
  yield takeLatest(fetchWatchlistRequest.type, handleFetchWatchlist);
  yield takeEvery(deleteWatchlistRequest.type, handleDeleteWatchlist);
  yield takeLatest(fetchMarketForWatchListRequest.type, handleFetchMarketForWatchList);

  while (true) {
    yield take('START_MARKET_DATA_UPDATES_WATCHLIST');
    const task = yield fork(handleUpdateWatchlistMarketData);
    yield take('STOP_MARKET_DATA_UPDATES_WATCHLIST');
    yield cancel(task);
  }
}